import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Search, ChevronDown } from 'lucide-react';
import api from '../../lib/api';

export default function CannedResponsePicker({ onSelect }) {
  const [open, setOpen]         = useState(false);
  const [responses, setResponses] = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (open && responses.length === 0) {
      setLoading(true);
      api.get('/canned')
        .then(d => setResponses(d.responses || []))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = responses.filter(r =>
    r.title.toLowerCase().includes(search.toLowerCase()) ||
    r.body.toLowerCase().includes(search.toLowerCase())
  );

  function handleSelect(r) {
    onSelect(r.body);
    setOpen(false);
    setSearch('');
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen(o => !o)}
        title="Insert canned response"
        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
      >
        <MessageSquare size={13} />
        Canned
        <ChevronDown size={11} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
          width: 320, maxHeight: 360, display: 'flex', flexDirection: 'column',
          zIndex: 100,
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search responses..."
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, width: '100%', color: 'var(--text)' }}
            />
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>Loading...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>No responses found</div>
            )}
            {!loading && filtered.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => handleSelect(r)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '9px 14px', border: 'none', background: 'transparent',
                  cursor: 'pointer', borderBottom: '1px solid var(--border)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{r.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.body}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
